import React, { createContext, useContext, useReducer, useEffect, use } from 'react';
import { Photo } from '@/types';
import { useToast } from '@/components/ui/use-toast';
import { DiscountRule } from '@/types';
import { supabase } from '@/lib/supabase';
import { useCallback } from 'react';
// Add this import at the top
function debounce(func, wait) {
  let timeout;
  return function(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}


// Define what a cart item looks like - it includes the photo and its quantity
interface CartItem {
  photo: Photo;
  originalPrice?: number;    // Add this property (optional)
  discountedPrice?: number;  // Add this property (optional)
  appliedDiscount?: number;  // Add this property (optional)
}



// Define the shape of our cart state
interface CartState {
  items: CartItem[];
  total: number;
}
// Helper function to apply discounts based on quantity
const applyDiscounts = async (items) => {
  try {
    // Group items by album_id
    const itemsByAlbum = {};
    
    items.forEach(item => {
      const albumId = item.photo.album_id;
      if (!albumId) return;
      
      if (!itemsByAlbum[albumId]) {
        itemsByAlbum[albumId] = [];
      }
      
      itemsByAlbum[albumId].push(item);
    });
    
    // For each album, fetch discount rules and apply if applicable
    const albumIds = Object.keys(itemsByAlbum);
    
    if (albumIds.length === 0) return items;
    
    // Fetch all discount rules for these albums
    const { data: discountRules } = await supabase
      .from('album_discounts')
      .select('*')
      .in('album_id', albumIds)
      .eq('active', true);
      
    if (!discountRules || discountRules.length === 0) return items;
    
    // Group discount rules by album_id
    const rulesByAlbum = {};
    discountRules.forEach(rule => {
      if (!rulesByAlbum[rule.album_id]) {
        rulesByAlbum[rule.album_id] = [];
      }
      rulesByAlbum[rule.album_id].push(rule);
    });
    
    // Apply discounts to each item based on applicable rules
    return items.map(item => {
      const albumId = item.photo.album_id;
      if (!albumId || !rulesByAlbum[albumId]) return item;
      
      const albumItems = itemsByAlbum[albumId];
      const itemCount = albumItems.length;
      
      // Find the highest applicable discount
      const applicableRules = rulesByAlbum[albumId]
        .filter(rule => itemCount >= rule.min_quantity)
        .sort((a, b) => b.discount_percentage - a.discount_percentage);
      
      if (applicableRules.length === 0) {
        // No discount applies - return item with original price
        return {
          ...item,
          originalPrice: item.photo.price,
          discountedPrice: item.photo.price, // Same as original
          appliedDiscount: 0                 // No discount
        };
      }
      
      // Apply the highest discount
      const bestDiscount = applicableRules[0];
      const discountFactor = 1 - (bestDiscount.discount_percentage / 100);
      
      // Apply discount to the item price
      return {
        ...item,
        originalPrice: item.photo.price,                    // Original price
        discountedPrice: item.photo.price * discountFactor, // Price after discount
        appliedDiscount: bestDiscount.discount_percentage   // Discount percentage
      };
    });
  } catch (error) {
    console.error('Error applying discounts:', error);
    return items;
  }
};

// Update the calculateTotal function to use discounted prices
const calculateTotal = (items) => {
  return items.reduce((sum, item) => {
    // Use discounted price if available, otherwise use the original price
    const price = item.discountedPrice !== undefined ? item.discountedPrice : item.photo.price;
    return sum + price ;
  }, 0);
};

const calculateDiscounts = (items: CartItem[], discountRules: DiscountRule[]) => {
  // Group items by photographer
  const photographerItems = items.reduce((acc, item) => {
    const photographerId = item.photo.user_id;
    if (!acc[photographerId]) {
      acc[photographerId] = [];
    }
    acc[photographerId].push(item);
    return acc;
  }, {});

  let totalDiscount = 0;
  let subtotal = 0;

  // Calculate discounts for each photographer's items
  Object.entries(photographerItems).forEach(([photographerId, items]) => {
    const quantity = items.length;
    const itemSubtotal = items.reduce((sum, item) => sum + item.photo.price, 0);
    subtotal += itemSubtotal;

    // Find the best applicable discount rule
    const applicableRules = discountRules
      .filter(rule => 
        rule.photographerId === photographerId && 
        quantity >= rule.minQuantity
      )
      .sort((a, b) => b.discountPercentage - a.discountPercentage);

    if (applicableRules.length > 0) {
      const bestRule = applicableRules[0];
      const discount = itemSubtotal * (bestRule.discountPercentage / 100);
      totalDiscount += discount;
    }
  });

  return {
    subtotal,
    discount: totalDiscount,
    total: subtotal - totalDiscount
  };
};

// Define all possible actions we can perform on the cart
type CartAction =
  | { type: 'ADD_ITEM'; payload: Photo }
  | { type: 'REMOVE_ITEM'; payload: string }
  | { type: 'CLEAR_CART' }
  // | { type: 'UPDATE_QUANTITY'; payload: { photoId: string; quantity: number } }
  | { type: 'LOAD_CART'; payload: CartState }
  | { type: 'UPDATE_DISCOUNTS'; payload: { items: CartItem[]; total: number } };

// Initialize an empty cart
const initialState: CartState = {
  items: [],
  total: 0,
};


// Load cart state from localStorage when the app starts
const loadInitialState = (): CartState => {
  if (typeof window === 'undefined') return initialState;
  
  const savedCart = localStorage.getItem('cart');
  return savedCart ? JSON.parse(savedCart) : initialState;
};

// The reducer function handles all cart state updates
const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD_ITEM': {
      // Find existing item
      const existingItemIndex = state.items.findIndex(
        item => item.photo.id === action.payload.id
      );
    
      // If item already exists, simply return current state (avoid adding duplicates)
      if (existingItemIndex >= 0) {
        return state;
      }
    
      // If it's a new item, add it to cart
      const updatedItems = [...state.items, { 
        photo: action.payload, 
        originalPrice: action.payload.price,
        discountedPrice: undefined,
        appliedDiscount: undefined
      }];
      
      return {
        items: updatedItems,
        total: calculateTotal(updatedItems),
      };
    }    
        // Add a new action type to your reducer
    case 'UPDATE_DISCOUNTS': {
      return {
        ...state,
        items: action.payload.items,
        total: action.payload.total
      };
    }

    case 'REMOVE_ITEM': {
      const updatedItems = state.items.filter(item => item.photo.id !== action.payload);
  
      // Return updated state without discounts - they will be recalculated by useEffect
      return {
        items: updatedItems.map(item => ({
          ...item,
          originalPrice: item.photo.price,
          discountedPrice: undefined, // Clear discount to force recalculation
          appliedDiscount: undefined  // Clear discount to force recalculation
        })),
        total: calculateTotal(updatedItems),
      };
    }

    // case 'UPDATE_QUANTITY': {
    //   const updatedItems = state.items.map(item =>
    //     item.photo.id === action.payload.photoId
    //       ? { ...item, quantity: action.payload.quantity }
    //       : item
    //   ).filter(item => item.quantity > 0); // Remove items with quantity 0

    //   return {
    //     items: updatedItems,
    //     total: calculateTotal(updatedItems),
    //   };
    // }

    case 'CLEAR_CART':
      return initialState;

    case 'LOAD_CART':
      return action.payload;

    default:
      return state;
  }
};

// Define the shape of our context
interface CartContextType {
  state: CartState;
  addToCart: (photo: Photo) => void;
  removeFromCart: (photoId: string) => void;
  updateQuantity: (photoId: string, quantity: number) => void;
  clearCart: () => void;
  itemCount: number;
}

// Create the context
const CartContext = createContext<CartContextType | undefined>(undefined);

// The provider component that wraps your app
export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState, loadInitialState);
  const { toast } = useToast();

  // Calculate total number of items in cart
  const itemCount = state.items.length;

  useEffect(() => {
    // Apply discounts whenever cart items change
    const updateDiscounts = async () => {
      if (state.items.length > 0) {
        // Only update if there are items that need discount calculation
        const needsUpdate = state.items.some(item => item.discountedPrice === undefined);
        
        if (needsUpdate) {
          try {
            const itemsWithDiscounts = await applyDiscounts(state.items);
            const newTotal = calculateTotal(itemsWithDiscounts);
      
            // Update the state with discounted items
            dispatch({ 
              type: 'UPDATE_DISCOUNTS', 
              payload: { 
                items: itemsWithDiscounts, 
                total: newTotal 
              } 
            });
          } catch (error) {
            console.error('Error updating discounts:', error);
          }
        }
      }
    };
    
    // Use a small timeout to ensure this runs after other state updates
    const timeoutId = setTimeout(updateDiscounts, 50);
    return () => clearTimeout(timeoutId);
  }, [state.items]);
    
  
  // Save cart state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(state));
  }, [state]);
// Add this to your CartProvider component
const addToCartDebounced = useCallback(
  debounce((photo: Photo) => {
    try {
      // Check if item already exists
      const alreadyExists = state.items.some(item => item.photo.id === photo.id);
      if (alreadyExists) {
        toast({
          title: "Already in Cart",
          description: "This photo is already in your cart",
        });
        return;
      }

      // Safely get the photographer name with fallbacks
      const photographerName = photo.user
        ? `${photo.photographer_first_name || ''} ${photo.photographer_last_name || ''}`
        : photo.photographer_first_name 
          ? `${photo.photographer_first_name} ${photo.photographer_last_name || ''}`
          : 'Unknown';
  
      // Add to cart with safe naming
      dispatch({ type: 'ADD_ITEM', payload: photo });
  
      toast({
        title: "Added to Cart",
        description: `Photo by ${photographerName} has been added to your cart`,
      });
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to add item to cart. Please try again.",
      });
    }
  }, 300),
  [state.items, toast, dispatch]
);

// Replace your addToCart function with:
const addToCart = (photo: Photo) => {
  addToCartDebounced(photo);
};
  
  const removeFromCart = (photoId: string) => {
    dispatch({ type: 'REMOVE_ITEM', payload: photoId });
      // Let the useEffect handle discount recalculation by letting it detect the item count change
      
    toast({
      title: "Removed from Cart",
      description: "Item has been removed from your cart",
    });
  };

  const updateQuantity = (photoId: string, quantity: number) => {
    if (quantity >= 0) {
      dispatch({ type: 'UPDATE_QUANTITY', payload: { photoId, quantity } });
    }
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
    toast({
      title: "Cart Cleared",
      description: "All items have been removed from your cart",
    });
  };

  const value = {
    state,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    itemCount,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

// Custom hook to use the cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};